import { useTranslation } from 'react-i18next';
import { XMarkIcon } from '@heroicons/react/20/solid';
import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';

import { NotificationType } from 'stores/notifications/notifications';
import { cn } from 'support/helpers/cn/cn';

import { useAutoDismissTimer } from './hooks/useAutoDismissTimer';

const COUNTDOWN_COLORS: Record<NotificationType, string> = {
  [NotificationType.success]: 'bg-procuros-green-500/50',
  [NotificationType.error]: 'bg-red-600/40',
};

type NotificationProps = {
  headline: string;
  description: string | undefined;
  type: NotificationType | undefined;
  /** Called by the close button and by the countdown running out. */
  onDismiss: () => void;
  /** `null` keeps the notification up until it is dismissed by hand. */
  autoDismissMs?: number | null;
  /** Freezes the countdown and its bar, banking the time left. */
  paused?: boolean;
};

export const AUTO_DISMISS_TIMEOUT = 10000;

export const Notification = ({
  headline,
  description,
  type,
  onDismiss,
  autoDismissMs = AUTO_DISMISS_TIMEOUT,
  paused = false,
}: NotificationProps) => {
  const { t } = useTranslation();

  useAutoDismissTimer(onDismiss, autoDismissMs, paused);

  return (
    <div className="relative flex w-full overflow-hidden rounded-lg bg-white shadow-lg ring-1 ring-black/5">
      <div className="w-0 flex-1 p-4">
        <div className="flex items-start">
          {type && (
            <div className="shrink-0">
              {type === NotificationType.success ? (
                <CheckCircleIcon className="size-6 text-procuros-green-500" aria-hidden="true" />
              ) : (
                <XCircleIcon className="size-6 text-red-600" aria-hidden="true" />
              )}
            </div>
          )}

          <div className="ml-3 w-0 flex-1">
            <p className="text-sm font-medium text-gray-900">{headline}</p>
            {description && <p className="mt-1 text-sm text-gray-500">{description}</p>}
          </div>
        </div>
      </div>

      <div className="ml-4 flex shrink items-center pr-5">
        <button
          type="button"
          className="flex rounded-md bg-white text-gray-400 hover:text-gray-500 focus-visible:ring-2 focus-visible:ring-procuros-green-400 focus-visible:ring-offset-2 focus-visible:outline-hidden"
          onClick={onDismiss}
        >
          <span className="sr-only">{t('close')}</span>
          <XMarkIcon className="size-5" aria-hidden="true" />
        </button>
      </div>

      {autoDismissMs !== null && (
        <div
          aria-hidden="true"
          className={cn(
            'absolute inset-x-0 bottom-0 h-1 origin-left animate-notification-countdown',
            type ? COUNTDOWN_COLORS[type] : 'bg-gray-400/40',
          )}
          style={{
            animationDuration: `${autoDismissMs}ms`,
            animationPlayState: paused ? 'paused' : 'running',
          }}
        />
      )}
    </div>
  );
};
